import React from 'react';
import profileImg from '../../assets/avatar-2.svg';
import ModalContainer from '../molecules/ModalContainer';
import ConnectAccountSteps from '../atoms/EmailAccoutDetail/ConnectAccountSteps';
import { timezones } from '../atoms/CreateCompaign/timezones';
import Select from '../molecules/Select';
import Field from '../atoms/Field';
import { StyledSheduleCampaign } from '../atoms/CreateCompaign/CreateCompaign.styles';
import {
  buildHourString,
  formatDateForDisplay,
  formatDateForInput,
  HOUR_OPTIONS,
  parseHourString,
  SCHEDULE_DAYS,
} from './emailScheduleUtils';
import {
  LaunchContainer,
  LaunchLeft,
  LaunchCard,
  LaunchRight,
  LaunchRightCard,
  LaunchSectionTitle,
  LaunchSectionSubtitle,
  LaunchFormGroup,
  LaunchInput,
  ProfileItem,
  ToggleSwitch,
  ConnectLinkedInButton,
  ProfilesLoading,
  LaunchDateInput,
  EmailSendingScheduleContent,
  EmailScheduleSection,
  EmailScheduleToggleCard,
} from './FlowBuilder.styles';

const FlowBuilderLaunch = ({
  campaignName,
  setCampaignName,
  description,
  setDescription,
  timezone,
  setTimezone,
  timeFrom,
  setTimeFrom,
  timeTo,
  setTimeTo,
  selectedDays,
  toggleDay,
  startingDate,
  setStartingDate,
  dailyLimit,
  setDailyLimit,
  showDailyLimitField,
  userProfile_loading,
  userProfile_data,
  activeProfiles,
  toggleProfile,
  isIframeOpen,
  setIsIframeOpen,
  hasEmailNodes,
  emailSchedule,
  onUpdateEmailSchedule,
}) => {
  const fromParsed = parseHourString(timeFrom);
  const toParsed = parseHourString(timeTo);
  const selectedTz = timezones.find(tz => tz.value === timezone) || null;

  const emailScheduleEnabled = !!emailSchedule?.enabled;
  const emailFromParsed = parseHourString(emailSchedule?.timeFrom);
  const emailToParsed = parseHourString(emailSchedule?.timeTo);
  const emailSelectedTz = timezones.find(tz => tz.value === emailSchedule?.timezone) || null;

  const patchEmailSchedule = updates => {
    onUpdateEmailSchedule(updates);
  };

  const toggleEmailScheduleDay = day => {
    const days = emailSchedule?.days || [];
    patchEmailSchedule({
      days: days.includes(day) ? days.filter(d => d !== day) : [...days, day],
    });
  };

  const renderEmailScheduleFields = () => (
    <>
      <div className="timezone-select">
        <Select
          label="Time Zone"
          colorBg
          selectSm
          rounded
          placeholder="Select Time Zone"
          options={timezones}
          value={emailSelectedTz}
          onChange={({ target: { value: opt } }) =>
            patchEmailSchedule({ timezone: opt?.value || '' })
          }
        />
      </div>

      <div className="choose-time-fields">
        <Select
          label="From"
          selectSm
          options={HOUR_OPTIONS}
          value={HOUR_OPTIONS.find(h => h.value === emailFromParsed.hour) || null}
          onChange={({ target: { value: opt } }) =>
            patchEmailSchedule({
              timeFrom: buildHourString(
                opt?.value || emailFromParsed.hour,
                emailFromParsed.period,
              ),
            })
          }
          suffix={
            <div className="time-period">
              <button
                type="button"
                className={emailFromParsed.period === 'am' ? 'active' : ''}
                onClick={() =>
                  patchEmailSchedule({
                    timeFrom: buildHourString(emailFromParsed.hour, 'am'),
                  })
                }
              >
                AM
              </button>
              <button
                type="button"
                className={emailFromParsed.period === 'pm' ? 'active' : ''}
                onClick={() =>
                  patchEmailSchedule({
                    timeFrom: buildHourString(emailFromParsed.hour, 'pm'),
                  })
                }
              >
                PM
              </button>
            </div>
          }
        />
        <Select
          label="To"
          selectSm
          options={HOUR_OPTIONS}
          value={HOUR_OPTIONS.find(h => h.value === emailToParsed.hour) || null}
          onChange={({ target: { value: opt } }) =>
            patchEmailSchedule({
              timeTo: buildHourString(
                opt?.value || emailToParsed.hour,
                emailToParsed.period,
              ),
            })
          }
          suffix={
            <div className="time-period">
              <button
                type="button"
                className={emailToParsed.period === 'am' ? 'active' : ''}
                onClick={() =>
                  patchEmailSchedule({
                    timeTo: buildHourString(emailToParsed.hour, 'am'),
                  })
                }
              >
                AM
              </button>
              <button
                type="button"
                className={emailToParsed.period === 'pm' ? 'active' : ''}
                onClick={() =>
                  patchEmailSchedule({
                    timeTo: buildHourString(emailToParsed.hour, 'pm'),
                  })
                }
              >
                PM
              </button>
            </div>
          }
        />
      </div>

      <span className="days-list-label">Days:</span>
      <div className="days-list">
        {SCHEDULE_DAYS.map(day => (
          <Field
            key={day}
            type="checkbox"
            rounded
            noMargin
            label={day}
            name={`email-${day.toLowerCase()}`}
            value={(emailSchedule?.days || []).includes(day)}
            onChange={() => toggleEmailScheduleDay(day)}
          />
        ))}
      </div>
    </>
  );

  return (
    <LaunchContainer>
      <LaunchLeft>
        <LaunchCard>
          <LaunchSectionTitle>Automation details</LaunchSectionTitle>
          <LaunchSectionSubtitle>Configure your workflow name and add a description</LaunchSectionSubtitle>

          <LaunchFormGroup>
            <label>Workflow Name</label>
            <LaunchInput
              type="text"
              placeholder="Enter workflow name..."
              value={campaignName}
              onChange={e => setCampaignName(e.target.value)}
            />
          </LaunchFormGroup>

          <LaunchFormGroup>
            <label>Description</label>
            <LaunchInput
              as="textarea"
              placeholder="Enter a short description..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={5}
            />
          </LaunchFormGroup>
        </LaunchCard>

        <LaunchCard>
          <LaunchSectionTitle>Campaign Schedule</LaunchSectionTitle>
          <LaunchSectionSubtitle>Configure the schedule for your workflow automation</LaunchSectionSubtitle>

          <StyledSheduleCampaign>
            {showDailyLimitField && (
              <LaunchFormGroup>
                <label>
                  Daily limit per email <span className="required">*</span>
                </label>
                <LaunchInput
                  type="number"
                  step={1}
                  min={1}
                  max={200}
                  placeholder="Enter daily limit"
                  value={dailyLimit}
                  onChange={e => setDailyLimit(e.target.value)}
                  required
                />
              </LaunchFormGroup>
            )}

            <div className="timezone-select">
              <Select
                label="Time Zone"
                colorBg
                selectSm
                rounded
                placeholder="Select Time Zone"
                options={timezones}
                value={selectedTz}
                onChange={({ target: { value: opt } }) => setTimezone(opt?.value || '')}
              />
            </div>

            <div className="choose-time-fields">
              <Select
                label="From"
                selectSm
                options={HOUR_OPTIONS}
                value={HOUR_OPTIONS.find(h => h.value === fromParsed.hour) || null}
                onChange={({ target: { value: opt } }) =>
                  setTimeFrom(buildHourString(opt?.value || fromParsed.hour, fromParsed.period))
                }
                suffix={
                  <div className="time-period">
                    <button
                      type="button"
                      className={fromParsed.period === 'am' ? 'active' : ''}
                      onClick={() => setTimeFrom(buildHourString(fromParsed.hour, 'am'))}>
                      AM
                    </button>
                    <button
                      type="button"
                      className={fromParsed.period === 'pm' ? 'active' : ''}
                      onClick={() => setTimeFrom(buildHourString(fromParsed.hour, 'pm'))}>
                      PM
                    </button>
                  </div>
                }
              />
              <Select
                label="To"
                selectSm
                options={HOUR_OPTIONS}
                value={HOUR_OPTIONS.find(h => h.value === toParsed.hour) || null}
                onChange={({ target: { value: opt } }) =>
                  setTimeTo(buildHourString(opt?.value || toParsed.hour, toParsed.period))
                }
                suffix={
                  <div className="time-period">
                    <button
                      type="button"
                      className={toParsed.period === 'am' ? 'active' : ''}
                      onClick={() => setTimeTo(buildHourString(toParsed.hour, 'am'))}>
                      AM
                    </button>
                    <button
                      type="button"
                      className={toParsed.period === 'pm' ? 'active' : ''}
                      onClick={() => setTimeTo(buildHourString(toParsed.hour, 'pm'))}>
                      PM
                    </button>
                  </div>
                }
              />
            </div>

            <span className="days-list-label">Days:</span>
            <div className="days-list">
              {SCHEDULE_DAYS.map(day => (
                <Field
                  key={day}
                  type="checkbox"
                  rounded
                  noMargin
                  label={day}
                  name={day.toLowerCase()}
                  value={selectedDays.includes(day)}
                  onChange={() => toggleDay(day)}
                />
              ))}
            </div>

            <LaunchFormGroup className="starting-date-group">
              <label>Starting Date</label>
              <LaunchDateInput
                type="date"
                value={formatDateForInput(startingDate)}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setStartingDate(formatDateForDisplay(e.target.value))}
              />
            </LaunchFormGroup>
          </StyledSheduleCampaign>

          {hasEmailNodes && (
            <EmailScheduleSection>
              <EmailScheduleToggleCard>
                <Field
                  type="checkbox"
                  rounded
                  noMargin
                  label="Seperate Schedule for Email Sending"
                  name="send-email-schedule"
                  value={emailScheduleEnabled}
                  onChange={() => patchEmailSchedule({ enabled: !emailScheduleEnabled })}
                />
              </EmailScheduleToggleCard>

              <EmailSendingScheduleContent $open={emailScheduleEnabled}>
                <div className="inner">
                  <div className="fields">
                    <StyledSheduleCampaign className="email-schedule-fields">
                      {renderEmailScheduleFields()}
                    </StyledSheduleCampaign>
                  </div>
                </div>
              </EmailSendingScheduleContent>
            </EmailScheduleSection>
          )}
        </LaunchCard>
      </LaunchLeft>

      <LaunchRight>
        <LaunchRightCard>
          <LaunchSectionTitle>Sender LinkedIn Profile</LaunchSectionTitle>
          <LaunchSectionSubtitle>
            Select one or more LinkedIn profiles to use as senders for this workflow
          </LaunchSectionSubtitle>

          {userProfile_loading ? (
            <ProfilesLoading>Loading profiles...</ProfilesLoading>
          ) : userProfile_data && userProfile_data.length > 0 ? (
            userProfile_data.map(profile => (
              <ProfileItem key={profile.id} $active={activeProfiles[profile.id]}>
                <div className="avatar">
                  <img
                    src={profile.profile_photo || profileImg}
                    alt={`${profile.firstName || ''} ${profile.lastName || ''}`}
                    onError={e => {
                      e.currentTarget.src = profileImg;
                    }}
                  />
                  <span className="linkedin-badge">in</span>
                </div>
                <span className="name">
                  {profile.firstName}
                  {profile.lastName ? ` ${profile.lastName}` : ''}
                </span>
                <ToggleSwitch $active={activeProfiles[profile.id]} onClick={() => toggleProfile(profile.id)} />
              </ProfileItem>
            ))
          ) : (
            <ProfilesLoading>No LinkedIn profiles found</ProfilesLoading>
          )}

          <ModalContainer
            noHead
            isCloseAble={false}
            padding="0"
            width={isIframeOpen ? 1439 : 1000}
            btnComponent={({ onClick }) => (
              <ConnectLinkedInButton type="button" onClick={onClick}>
                Connect New LinkedIn Account
                <span className="li-icon">in</span>
              </ConnectLinkedInButton>
            )}
            content={({ onClose }) => (
              <ConnectAccountSteps
                onClose={() => {
                  onClose();
                  setIsIframeOpen(false);
                }}
                onIframeStateChange={setIsIframeOpen}
              />
            )}
          />
        </LaunchRightCard>
      </LaunchRight>
    </LaunchContainer>
  );
};

export default FlowBuilderLaunch;
